// Orders tab: prescriptions grouped by visit date, as cards.
//
// Rows collapse to the two things that identify a prescription at a glance —
// drug name and pharmacy — because a patient with many visits produces a wall
// of text otherwise. Everything else (directions, quantity, refills, days
// supply, dates, pharmacy address) expands per row on click.
import { useEffect, useState } from "react";
import { usePatientContext } from "Context/Patient";
import { Spinner } from "Components/Common/LoadingSpinner/Spinner";
import { ApiError } from "Components/Common/ApiError";
import { errorFormatter } from "Helpers/ErrorMsgHandler";
import { GetPatientOrders } from "Services/API/Patients.service";
import { formatDate2 } from "Helpers/DateFormat";
import { None } from "Components/Patients/EditViewPatient/Forms/DetailSection/DetailSection";
import { getMedicationStatus, getPrescriptionStatus } from "Helpers/Prescriptions";
// Same formatter the per-visit order view uses, so a pharmacy number reads
// identically on both screens.
import formatPhoneNumber from "Helpers/PhoneNumber";
import ClassNames from "Helpers/Common";
import "Components/Patients/EditViewPatient/Forms/FormCommonCss/Form.css";
import "Components/Patients/EditViewPatient/Forms/PaymentsTable/PaymentsTable.css";
import "Colors.css";

// Label and value share a size — the gray already marks which is which, so
// the smaller type was doing nothing the color wasn't.
const LABEL_CSS = "text-sm leading-5 font-medium light_grayish_navy";
const VALUE_CSS = "text-sm leading-5 font-normal sapphire";

// Same helper (and therefore the same "08/20/26 5:33am" Pacific format) the
// Visits tab uses for its Created column, so a prescription and the visit it
// came from read as the same moment in time.
const fmtDate = (d) => formatDate2(d, "MM/DD/YY h:mma", "America/Los_Angeles", null);

// Optional fields arrive null/""/"null" from older records. Missing values
// render as the grey "None" everywhere on these pages, never as a dash or an
// abbreviation the reader has to decode.
const isBlank = (v) => {
  const t = typeof v === "string" ? v.trim() : v;
  return t === null || t === undefined || t === "" || t === "null";
};

const val = (v) => (isBlank(v) ? <None /> : v);

const pharmacyName = (pharmacy) => {
  const name = pharmacy ? pharmacy.storeName || pharmacy.name : null;
  return isBlank(name) ? <None /> : name;
};

// The API resolves order.pharmacy by looking the DoseSpot id up in our own
// pharmacy collection, which only holds pharmacies patients picked in the app.
// A prescription routed or changed inside DoseSpot therefore comes back
// unresolved — and rendering that as a plain "None" reads as "no pharmacy",
// which is wrong and dangerous: the script was sent somewhere real. Say what
// we actually know, and show the id so it can be looked up in DoseSpot.
const UnresolvedPharmacy = ({ pharmacyId }) => (
  <span className="flex flex-col">
    <span className="light_grayish_navy">Not in directory</span>
    <span className="light_grayish_navy">DoseSpot ID {pharmacyId}</span>
  </span>
);

const hasPharmacyId = (order) => !isBlank(order?.pharmacyId);

// Label and value on one line, with the labels sharing a fixed width so the
// values form a single aligned column down the card.
const DetailLine = ({ label, children }) => (
  <div className="flex gap-3 items-baseline">
    <span className={`${LABEL_CSS} w-40 flex-none`}>{label}</span>
    <span className={VALUE_CSS}>{children}</span>
  </div>
);

// A prescription that errored is the one thing on this screen worth
// interrupting a scan for; a delivered one is worth confirming at a glance.
// Everything else stays plain so those two read as signal.
const STATUS_TONE = {
  Error: "font-semibold text-red-600",
  EpcsError: "font-semibold text-red-600",
  Deleted: "font-semibold text-red-600",
  eRxSent: "font-semibold text-green-cyan",
  PharmacyVerified: "font-semibold text-green-cyan",
  Completed: "font-semibold text-green-cyan",
  Active: "font-semibold text-green-cyan",
  Cancelled: "font-semibold text-red-600",
  CancelDenied: "font-semibold text-red-600",
};

const StatusValue = ({ status }) => {
  if (isBlank(status)) return <None />;
  return <span className={STATUS_TONE[status] || ""}>{status}</span>;
};

// `open` is owned by the visit group: a patient reading one prescription from
// a visit is almost always comparing it with the others written that day, so
// expanding one expands them all rather than making them click each in turn.
const OrderRow = ({ order, doctorName, open, onToggle, index }) => {
  const pharmacy = order.pharmacy;
  const line2 = pharmacy
    ? [pharmacy.city, pharmacy.state, pharmacy.zipCode].filter(Boolean).join(", ")
    : "";

  return (
    <div>
      {/* The whole row is the toggle, so there is no small hit target. */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-4 py-3 text-left"
      >
        {/* Numbered like the per-visit order view — with the whole group
            expanding at once, a per-row chevron implied a toggle each row
            no longer owns, and the count is what an admin reads out loud. */}
        <span className="text-sm light_grayish_navy w-5 flex-none">{index + 1}.</span>
        <span className="text-base font-semibold sapphire flex-1 truncate">
          {val(order.displayName)}
        </span>
        <span className="text-sm light_grayish_navy truncate max-w-[45%] text-right">
          {pharmacy || !hasPharmacyId(order)
            ? pharmacyName(pharmacy)
            : `DoseSpot ID ${order.pharmacyId}`}
        </span>
      </button>

      {/* One field per line, label then value — the shape the per-visit order
          view uses. Reading a prescription is a top-to-bottom task (what, how
          much, how often, who), which a 4-across grid broke into a zig-zag.
          Directions/refills/days-supply/written/effective/substitutions/
          pharmacy-notes were dropped as noise. pl-12 below = the header's px-4
          + the number's w-5 + gap-3, so the first label sits exactly under the
          medication name. */}
      {open && (
        <div className="px-4 pb-4 pl-12 flex flex-col gap-2">
          {/* Dosespot sends the two statuses as integers — show the same words
              the per-visit order view does, not the raw codes. */}
          <DetailLine label="Prescription Status">
            <StatusValue status={getPrescriptionStatus(order.status)} />
          </DetailLine>
          <DetailLine label="Medication Status">
            <StatusValue status={getMedicationStatus(order.medicationStatus)} />
          </DetailLine>
          <DetailLine label="Dosage">{val(order.dosage || order.strength)}</DetailLine>
          <DetailLine label="Quantity">
            {val(order.quantity)}
            {order.dispenseUnitDescription ? ` ${order.dispenseUnitDescription}` : ""}
          </DetailLine>
          {/* The raw payload calls the sig "directions"; the per-visit view
              surfaces it as Frequency. */}
          <DetailLine label="Frequency">
            {val(order.frequency || order.directions)}
          </DetailLine>
          {/* Prescriber comes from the visit the order belongs to. */}
          <DetailLine label="Provider">{val(doctorName)}</DetailLine>
          <DetailLine label="Pharmacy">
            {pharmacy ? (
              <span className="flex flex-col">
                <span>{pharmacyName(pharmacy)}</span>
                {pharmacy.address1 && <span>{pharmacy.address1}</span>}
                {line2 && <span>{line2}</span>}
                {pharmacy.primaryPhone && (
                  <span>{formatPhoneNumber(String(pharmacy.primaryPhone))}</span>
                )}
              </span>
            ) : hasPharmacyId(order) ? (
              <UnresolvedPharmacy pharmacyId={order.pharmacyId} />
            ) : (
              <None />
            )}
          </DetailLine>
        </div>
      )}
    </div>
  );
};

// One card per visit date. Visits are already newest-first from the API.
// White on white with hairline rules — the old gray card, gray header and
// gray row boxes stacked three shades of gray behind two lines of text.
const VisitCard = ({ group }) => {
  const [open, setOpen] = useState(false);

  return (
    // Same surface treatment the shared Table gives the Payments tab, so a
    // visit card and a table read as the same kind of object.
    <div className="shadow border-b border-gray-200 sm:rounded-lg bg-white overflow-hidden">
      {/* bg-gray-50 is the same fill every table <thead> uses — this row is
          the card's header, so it gets the header treatment, not the rows.
          The shadow sits on the card itself, so the whole thing lifts. */}
      <div
        className={ClassNames(
          "flex items-baseline gap-3 flex-wrap p-4 border-b border-gray-200",
          // Expanded card gets the same green the tables use to mark the row
          // you acted on, so an open card is findable in a long list.
          open ? "active-row" : "bg-gray-50"
        )}
      >
        <span className="text-sm font-semibold sapphire">
          {fmtDate(group.visitEndedAt || group.visitStartedAt) || <None />}
        </span>
        {/* Who wrote this visit's prescriptions. The count it replaced was
            already obvious from the numbered rows below. */}
        <span className="text-sm bali_hai">{group.doctorName || ""}</span>
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className="ml-auto text-xs text-blue-chill"
        >
          {open ? "Hide details" : "Show details"}
        </button>
      </div>
      {/* Thicker rule between prescriptions: with each one now a tall stacked
          column, a hairline read as just more row spacing. */}
      <div className="divide-y-2 divide-gray-200">
        {group.orders.map((o, i) => (
          <OrderRow
            key={o.id}
            order={o}
            index={i}
            doctorName={group.doctorName}
            open={open}
            onToggle={() => setOpen((prev) => !prev)}
          />
        ))}
      </div>
    </div>
  );
};

export function OrdersFormView({ setActiveTabIndex }) {
  const { getPatientData } = usePatientContext();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState("");

  useEffect(() => {
    const { id } = getPatientData() || {};
    if (!id) return;
    setLoading(true);
    GetPatientOrders(id)
      .then((res) => setGroups(res?.data || []))
      .catch((err) => {
        console.error("[ORDERS][API_ERROR] -", err);
        setApiError(errorFormatter(err?.response?.data?.errors || err?.message));
      })
      .finally(() => setLoading(false));
  }, [getPatientData]);

  if (loading) return <Spinner />;

  return (
    // Same shell as the Payments tab: fixed-height white panel that scrolls
    // its own content, so the tab nav stays put on long lists.
    <div className="TableWrapper flex flex-col gap-4">
      {apiError && <ApiError error={apiError} setApiError={setApiError} />}

      {groups.length > 0 ? (
        <div className="flex flex-col gap-4">
          {groups.map((g) => (
            <VisitCard key={g.visitId} group={g} />
          ))}
        </div>
      ) : (
        <None block />
      )}

    </div>
  );
}
